import { useEffect, useState } from "react";
import styled from "styled-components";
import DatePicker, { registerLocale } from "react-datepicker";
import fi from "date-fns/locale/fi";

import i18n from "../../../i18n/config";
import { CloseApplicationIcon, InfoForPersonalApplicationIcon } from "../modal/modalIcons";
import { BasicInput } from "../../../styles/basicComponents";
import { BatchApplication } from "../../../api/models/BatchApplication";
import { useTheme, useVariables } from "../../../features/context/CustomerContext";
import useCustomTranslation from "../../../features/hooks/CustomUseTranslation";

const MainDiv = styled.div`
    display: block;
    place-content: center;
    @media screen and (max-width: 850px) {
        margin: 0;
    }
`
const InputContainer = styled.div`
    position: relative;
    padding: 17px;
    max-width: 356px;
    margin: auto;
    @media screen and (max-width: 650px) {
        padding-left: 0;
        padding-right: ${props => props.theme.hasError ? "50px" : "0px"};
    }
`

const ApplicationContainer = styled.div`
    position: relative;
    background-color: ${props => props.theme.hasError ? "#D50000" : "#EEEEEE"};
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 10px;
    @media screen and (max-width: 850px) {
        border-top-left-radius: 15px;
        border-bottom-left-radius: 10px;
    }
`

const ApplicationInputContainer = styled.div`
    display: flex;
    padding: 17px;
    margin-left: ${props => props.theme.hasError ? "70px" : "0px"};
    margin-right: ${props => props.theme.hasError ? "0px" : "70px"};
    background-color: #F4F5F5;
    border-top-right-radius: 10px;
    border-bottom-right-radius: ${props => props.theme.hasError ? "0px" : "10px"};
    border-top-left-radius: ${props => props.theme.hasError ? "0px" : "10px"};
    border-bottom-left-radius: ${props => props.theme.hasError ? "0px" : "10px"};
    @media screen and (max-width: 850px) {
        flex-direction: column;
        border-bottom-left-radius: 10px;
        margin-left: 0;
        margin-bottom: ${props => props.theme.hasError ? "40px" : "0px"};
        border-top-left-radius: 10px;
        border-bottom-left-radius: ${props => props.theme.hasError ? "0px" : "10px"};
    }
    @media screen and (max-width: 600px) {
        margin-right: ${props => props.theme.hasError ? "0px" : "50px"};
    }
`

const InputTitle = styled.label`
    position: absolute;
    top: 11px;
    margin-left: 40px;
    background-color: #F4F5F5;
    padding: 0 10px 0 10px;
    color: #686868;
    z-index: 99;
    @media screen and (max-width: 600px) {
        margin-left: 20px;
    }
`
const Input = styled(BasicInput)`
    width: 100%;
    background-color: #F4F5F5 !important;
    border-color: #96008F;
    padding-left: 50px;
    @media screen and (max-width: 600px) {
        padding-left: 30px;
    }
`

const DatesContainer = styled.div`
@media screen and (min-width: 471px) {
    flex: 1;
}
`

const ReceiverContainer = styled.div`
@media screen and (min-width: 471px) {
    flex: 2;
}
`
const FieldValue = styled.span`
    display: inline;
    > input {
        border: 2px solid ${props => props.theme.brandColor1};
    }
`
const DatePickerWrapper = styled.div`
    width: 100%;
    display: inline-block;
    padding-right: 0;
    > div.react-datepicker-wrapper > div.react-datepicker__input-container input {
        border: 2px solid ${props => props.theme.brandColor1};
    }
`
const ErrorMessageContainer = styled.div`
    padding: 17px;
    margin-left: ${props => props.theme.hasError ? "70px" : "0px"};
    background-color: #EEEEEE;
    border-bottom-right-radius: 10px;
    border-bottom-left-radius: 0px;
    @media screen and (max-width: 850px) {
        border-bottom-left-radius: 10px;
        margin-left: 0;
        margin-bottom: ${props => props.theme.hasError ? "40px" : "0px"};
    }
`
const Error = styled.span`
    display: inline;
    padding: 20px 0px 20px 0px;
`
const ErrorSecondMsg = styled.span`
    display: inline;
    padding: 20px 0px 0px 0px;
`
const ErrorRed = styled.span`
    color: red;
    padding-top: 10px;
    font-weight: bold;
`
const ErrorList = styled.li`
    padding-left: 10px;
`
const Field = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    margin: auto;
    background: #EEEEEE;
    text-align: left;
`
export interface NewApplicationProps {
    id: number;
    application: BatchApplication;
    setApplication: (application: BatchApplication, id: number) => void;
    deleteApplication: (id: number) => void;
    currentUser: string | undefined;
}

const NewApplication: React.FC<NewApplicationProps> = (props) => {
    var currentDate = new Date();
    var maxYearsFromNow = new Date();
    const variables = useVariables();
    maxYearsFromNow.setFullYear(currentDate.getFullYear() + variables.AUTHORIZATION_MAX_PERIOD_OF_VALIDITY_IN_YEARS);

    registerLocale("fi", fi);

    const [maximumDate, setMaximumDate] = useState<Date>(maxYearsFromNow);
    const [ownUsernameError, setOwnUsernameError] = useState<boolean>(false);

    const { t } = useCustomTranslation();
    const theme = useTheme();

    useEffect(() => {
        if (props.application.requesteeUsername === props.currentUser) {
            setOwnUsernameError(true);
        }
        else {
            setOwnUsernameError(false);
        }
        // eslint-disable-next-line
    }, [props.application.requesteeUsername])


    useEffect(() => {
        maximumDate.setDate(props.application.startDate ? props.application.startDate.getDate() : maxYearsFromNow.getDate());
        maximumDate.setMonth(props.application.startDate ? props.application.startDate.getMonth() : maxYearsFromNow.getMonth());
        maximumDate.setFullYear(props.application.startDate ? props.application.startDate.getFullYear() + variables.AUTHORIZATION_MAX_PERIOD_OF_VALIDITY_IN_YEARS : maxYearsFromNow.getFullYear() + variables.AUTHORIZATION_MAX_PERIOD_OF_VALIDITY_IN_YEARS);
        setMaximumDate(maximumDate);
        if (props.application.endDate && maximumDate < props.application.endDate) {
            props.application.endDate.setDate(maximumDate.getDate());
            props.application.endDate.setMonth(maximumDate.getMonth());
            props.application.endDate.setFullYear(maximumDate.getFullYear());
            props.setApplication({ ...props.application, requesteeCustomerNumber: props.application.requesteeCustomerNumber, requesteeUsername: props.application.requesteeUsername, startDate: props.application.startDate, endDate: maximumDate }, props.id)
        }
        // eslint-disable-next-line
    }, [props.application.startDate]);

    return (
        <MainDiv>
            <ApplicationContainer theme={{ hasError: props.application.errorCode || ownUsernameError }}>
                <ApplicationInputContainer theme={{ hasError: props.application.errorCode || ownUsernameError }}>
                    <DatesContainer>
                        <InputContainer theme={{ hasError: props.application.errorCode || ownUsernameError }}>
                            <InputTitle htmlFor="startDatePicker">{t("Start date")}</InputTitle>
                            <DatePickerWrapper theme={theme}>
                                <DatePicker
                                    className="newApplicationDatePicker"
                                    id="startDatePicker"
                                    locale={i18n.language}
                                    minDate={currentDate}
                                    calendarStartDay={1}
                                    dateFormat={'dd.MM.yyyy'}
                                    selected={props.application.startDate}
                                    onChange={(date: Date | null) => date && props.setApplication({ ...props.application, requesteeCustomerNumber: props.application.requesteeCustomerNumber, requesteeUsername: props.application.requesteeUsername, startDate: date, endDate: props.application.endDate }, props.id)} />
                            </DatePickerWrapper>
                        </InputContainer>
                        <InputContainer theme={{ hasError: props.application.errorCode || ownUsernameError }}>
                            <InputTitle htmlFor="endDatePicker">{t("End date")}</InputTitle>
                            <DatePickerWrapper theme={theme}>
                                <DatePicker
                                    className="newApplicationDatePicker"
                                    id="endDatePicker"
                                    locale={i18n.language}
                                    minDate={props.application.startDate}
                                    maxDate={maximumDate}
                                    calendarStartDay={1}
                                    dateFormat={'dd.MM.yyyy'}
                                    selected={props.application.endDate}
                                    onChange={(date: Date | null) => date && props.setApplication({ ...props.application, requesteeCustomerNumber: props.application.requesteeCustomerNumber, requesteeUsername: props.application.requesteeUsername, startDate: props.application.startDate, endDate: date }, props.id)} />
                            </DatePickerWrapper>
                        </InputContainer>
                    </DatesContainer>
                    <ReceiverContainer>
                        <InputContainer theme={{ hasError: props.application.errorCode || ownUsernameError }}>
                            <InputTitle htmlFor="receiverEmailInput">{t("Receiver email")}</InputTitle>
                            <FieldValue theme={theme}>
                                <Input id="receiverEmailInput"
                                    type="text"
                                    value={props.application.requesteeUsername}
                                    onChange={(e) => props.setApplication({ ...props.application, requesteeCustomerNumber: props.application.requesteeCustomerNumber, requesteeUsername: (e.target.value) }, props.id)} />
                            </FieldValue>
                        </InputContainer>
                        <InputContainer theme={{ hasError: props.application.errorCode || ownUsernameError }}>
                            <InputTitle htmlFor="requestedCustomerInput">{t("Requested customer")}</InputTitle>
                            <FieldValue theme={theme}>
                                <Input id="requestedCustomerInput"
                                    type="number"
                                    value={props.application.requesteeCustomerNumber}
                                    onChange={(e) => props.setApplication({ ...props.application, requesteeUsername: props.application.requesteeUsername, requesteeCustomerNumber: (e.target.value.replace(/\D/,''))}, props.id)} />
                            </FieldValue>
                        </InputContainer>
                    </ReceiverContainer>
                    <CloseApplicationIcon onClick={() => props.deleteApplication(props.id)} />
                </ApplicationInputContainer>
                {(props.application.errorCode || ownUsernameError) &&
                    <>
                        <InfoForPersonalApplicationIcon />
                        <ErrorMessageContainer theme={{ hasError: props.application.errorCode || ownUsernameError }}>
                            <Field>
                                {ownUsernameError ?
                                    <ErrorRed>{t("ErrorReceiverSenderSameEmail")}</ErrorRed> : <br />}
                            </Field>
                            {props.application.errorCode &&
                                <Field>
                                    <ErrorRed>{t("ErrorMsgSubtitle")}</ErrorRed>
                                    <Error>{t("ErrorMsgFirst")}</Error>
                                    <ErrorList>{t("ErrorMsgListFirst")}</ErrorList>
                                    <ErrorList>{t("ErrorMsgListSecond")}</ErrorList>
                                    <ErrorList>{t("ErrorMsgListThird")}</ErrorList>
                                    <ErrorSecondMsg>{t("ErrorMsgSecond")}</ErrorSecondMsg>
                                    <Error>{t("ErrorMsgThird")}</Error>
                                </Field>}
                        </ErrorMessageContainer>
                    </>}
            </ApplicationContainer>
        </MainDiv>)
}

export default NewApplication